/**
 * Project Creation Wizard — Phase 10
 * Guided step-by-step flow matching user's hand-drawn diagram:
 * 1. Module + Subsystems
 * 2. Project info (Name, Company, Contact)
 * 3. Dates (Start / End)
 * 4. SOW + Documents
 * 5. Review & Create
 *
 * Users can toggle between this Wizard mode and the free-form modal
 * via the "Modo Guiado / Modo Libre" switch on Projects page.
 */
import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ChevronLeft, ChevronRight, Check, Loader2,
  Building2, Mail, Phone, Calendar, FileText, UploadCloud, Sparkles,
} from "lucide-react";
import api from "@/lib/api";

const STEPS_ES = [
  "Módulo y Subsistemas",
  "Información del Proyecto",
  "Cliente y Fechas",
  "SOW y Documentos",
  "Revisión Final",
];
const STEPS_EN = [
  "Module & Subsystems",
  "Project Info",
  "Client & Dates",
  "SOW & Documents",
  "Final Review",
];

export default function ProjectWizard() {
  const { i18n } = useTranslation();
  const es = i18n.language?.startsWith("es");
  const STEPS = es ? STEPS_ES : STEPS_EN;
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [creating, setCreating] = useState(false);
  const [subsystems, setSubsystems] = useState([]);

  const [f, setF] = useState({
    module: "low_voltage",
    subsystems: [],
    name: "",
    company: "",
    location: "Florida",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    start_date: "",
    end_date: "",
    sow_text: "",
    description: "",
  });
  const update = (k, v) => setF(p => ({ ...p, [k]: v }));
  const toggleSub = (s) => update("subsystems",
    f.subsystems.includes(s) ? f.subsystems.filter(x => x !== s) : [...f.subsystems, s]);

  useEffect(() => {
    api.get("/subsystems").then(r => setSubsystems(r.data.subsystems || []));
  }, []);

  const canNext = () => {
    if (step === 0) return f.module && f.subsystems.length > 0;
    if (step === 1) return f.name.trim().length >= 2;
    if (step === 2) return true; // dates optional
    if (step === 3) return true; // sow optional
    return true;
  };

  const create = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/projects", {
        ...f,
        creation_mode: "wizard",
      });
      navigate(`/projects/${data.id}`);
    } catch (e) {
      alert(e?.response?.data?.detail || "Error");
    } finally { setCreating(false); }
  };

  const lvSubs = subsystems.filter(s => s.category === "low_voltage");
  const elSubs = subsystems.filter(s => s.category === "electrical");
  const visibleSubs = f.module === "electrical" ? elSubs
                     : f.module === "low_voltage" ? lvSubs
                     : subsystems;

  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="project-wizard">
      <Link to="/projects"
        className="text-xs font-mono uppercase tracking-wider text-slate-500 hover:text-[#003366] inline-flex items-center gap-1 mb-2">
        <ChevronLeft size={12} /> {es ? "Cancelar" : "Cancel"}
      </Link>

      <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
        {es ? "Nuevo Proyecto — Modo Guiado" : "New Project — Guided Mode"}
      </h1>
      <p className="text-sm text-slate-600 mt-1">
        {es ? "Crea un proyecto paso a paso, con los campos y subsistemas pre-configurados."
            : "Create a project step-by-step with pre-configured fields and subsystems."}
      </p>

      {/* Stepper */}
      <div className="flex items-center gap-1 mt-6 mb-6">
        {STEPS.map((lbl, i) => (
          <div key={i} className="flex-1 flex items-center gap-1">
            <div className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-mono font-semibold ${
              i === step ? "bg-[#003366] text-white shadow" :
              i < step ? "bg-emerald-500 text-white" :
              "bg-slate-200 text-slate-500"
            }`} data-testid={`wizard-step-${i}`}>
              {i < step ? <Check size={13} /> : i + 1}
            </div>
            <div className={`text-xs font-mono uppercase tracking-wide flex-1 truncate ${
              i === step ? "text-[#003366] font-semibold" : "text-slate-500"
            }`}>{lbl}</div>
            {i < STEPS.length - 1 && <div className="w-3 h-px bg-slate-200" />}
          </div>
        ))}
      </div>

      <div className="iqes-card p-6">
        {/* STEP 0 — Module + Subsystems */}
        {step === 0 && (
          <div>
            <h2 className="text-lg font-semibold text-slate-900 mb-4">
              {es ? "¿Qué tipo de proyecto?" : "What type of project?"}
            </h2>
            <div className="grid grid-cols-3 gap-3 mb-6" data-testid="wizard-module-picker">
              {[
                { k: "low_voltage", es: "Bajo Voltaje", en: "Low Voltage", color: "#003366" },
                { k: "electrical", es: "Eléctrico", en: "Electrical", color: "#EAB308" },
                { k: "all", es: "Ambos", en: "Both", color: "#64748B" },
              ].map(m => (
                <button key={m.k}
                  data-testid={`module-option-${m.k}`}
                  onClick={() => { update("module", m.k); update("subsystems", []); }}
                  className={`rounded-2xl border-2 p-5 text-left transition-all ${
                    f.module === m.k
                      ? "border-[#003366] bg-[#003366]/5 shadow"
                      : "border-slate-200 hover:border-slate-300"
                  }`}>
                  <div className="w-3 h-3 rounded-full mb-2" style={{ backgroundColor: m.color }} />
                  <div className="font-semibold text-slate-900">{es ? m.es : m.en}</div>
                </button>
              ))}
            </div>

            <h3 className="text-sm font-semibold text-slate-900 mb-2">
              {es ? "Subsistemas a incluir" : "Subsystems to include"}
              <span className="ml-2 text-xs font-mono text-slate-500">
                ({f.subsystems.length} {es ? "seleccionados" : "selected"})
              </span>
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="wizard-subsystems">
              {visibleSubs.map(s => {
                const active = f.subsystems.includes(s.key);
                return (
                  <button key={s.key}
                    data-testid={`subsystem-${s.key}`}
                    onClick={() => toggleSub(s.key)}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm transition-colors ${
                      active ? "border-[#003366] bg-[#003366]/5 text-slate-900"
                             : "border-slate-200 bg-white hover:bg-slate-50 text-slate-700"
                    }`}>
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: s.color }} />
                    <span className="truncate">{es ? s.label_es : s.label_en}</span>
                    {active && <Check size={12} className="ml-auto text-emerald-600 flex-shrink-0" />}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* STEP 1 — Name + Company + Location */}
        {step === 1 && (
          <div>
            <h2 className="text-lg font-semibold text-slate-900 mb-4">
              {es ? "Datos del proyecto" : "Project data"}
            </h2>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-mono uppercase tracking-wider text-slate-500 block mb-1">
                  {es ? "Nombre del proyecto *" : "Project name *"}
                </label>
                <input value={f.name} onChange={e => update("name", e.target.value)}
                  data-testid="wizard-name-input"
                  placeholder={es ? "Tampa Office HQ - Fase 1" : "Tampa Office HQ - Phase 1"}
                  className="iqes-input" autoFocus />
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-wider text-slate-500 block mb-1">
                  <Building2 size={11} className="inline mr-1" /> {es ? "Compañía" : "Company"}
                </label>
                <input value={f.company} onChange={e => update("company", e.target.value)}
                  data-testid="wizard-company-input"
                  placeholder="Acme Corp"
                  className="iqes-input" />
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-wider text-slate-500 block mb-1">
                  {es ? "Ubicación" : "Location"}
                </label>
                <input value={f.location} onChange={e => update("location", e.target.value)}
                  data-testid="wizard-location-input"
                  placeholder="Tampa, FL" className="iqes-input" />
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-wider text-slate-500 block mb-1">
                  {es ? "Notas (opcional)" : "Notes (optional)"}
                </label>
                <textarea value={f.description} onChange={e => update("description", e.target.value)}
                  rows="3" className="iqes-input font-sans"
                  placeholder={es ? "32000 sqft, 120 usuarios, techo falso..." : "32000 sqft, 120 users, drop ceiling..."} />
              </div>
            </div>
          </div>
        )}

        {/* STEP 2 — Contact + Dates */}
        {step === 2 && (
          <div>
            <h2 className="text-lg font-semibold text-slate-900 mb-4">
              {es ? "Cliente y cronograma" : "Client & schedule"}
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-mono uppercase tracking-wider text-slate-500 block mb-1">
                  {es ? "Nombre del contacto" : "Contact name"}
                </label>
                <input value={f.contact_name} onChange={e => update("contact_name", e.target.value)}
                  data-testid="wizard-contact-name-input"
                  className="iqes-input" placeholder="Jane Doe" />
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-wider text-slate-500 block mb-1">
                  <Mail size={11} className="inline mr-1" /> {es ? "Correo del contacto" : "Contact email"}
                </label>
                <input type="email" value={f.contact_email} onChange={e => update("contact_email", e.target.value)}
                  data-testid="wizard-contact-email-input"
                  className="iqes-input" placeholder="jane@acme.com" />
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-wider text-slate-500 block mb-1">
                  <Phone size={11} className="inline mr-1" /> {es ? "Teléfono" : "Phone"}
                </label>
                <input value={f.contact_phone} onChange={e => update("contact_phone", e.target.value)}
                  data-testid="wizard-contact-phone-input"
                  className="iqes-input" placeholder="+1-813-555-0123" />
              </div>
              <div></div>
              <div>
                <label className="text-xs font-mono uppercase tracking-wider text-slate-500 block mb-1">
                  <Calendar size={11} className="inline mr-1" /> {es ? "Fecha inicio" : "Start date"}
                </label>
                <input type="date" value={f.start_date} onChange={e => update("start_date", e.target.value)}
                  data-testid="wizard-start-date-input"
                  className="iqes-input" />
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-wider text-slate-500 block mb-1">
                  <Calendar size={11} className="inline mr-1" /> {es ? "Fecha fin" : "End date"}
                </label>
                <input type="date" value={f.end_date} onChange={e => update("end_date", e.target.value)}
                  data-testid="wizard-end-date-input"
                  className="iqes-input" />
              </div>
            </div>
          </div>
        )}

        {/* STEP 3 — SOW */}
        {step === 3 && (
          <div>
            <h2 className="text-lg font-semibold text-slate-900 mb-4">
              {es ? "Scope of Work" : "Scope of Work"}
            </h2>
            <label className="text-xs font-mono uppercase tracking-wider text-slate-500 block mb-1">
              <FileText size={11} className="inline mr-1" /> {es ? "Pega tu SOW aquí (opcional)" : "Paste your SOW here (optional)"}
            </label>
            <textarea value={f.sow_text} onChange={e => update("sow_text", e.target.value)}
              data-testid="wizard-sow-input"
              rows="12"
              className="iqes-input font-mono text-xs leading-relaxed"
              placeholder={es
                ? "1. Scope…\n2. Equipos a instalar…\n3. Exclusiones…\n4. Tiempos…"
                : "1. Scope…\n2. Equipment to install…\n3. Exclusions…\n4. Timeline…"} />
            <div className="mt-3 flex items-start gap-2 text-xs bg-blue-50 border border-blue-200 rounded-xl p-3">
              <Sparkles size={14} className="text-blue-600 mt-0.5" />
              <p className="text-blue-900">
                {es
                  ? "Podrás subir planos, fotos y videos del sitio después de crear el proyecto, en la pestaña Analizar."
                  : "You'll be able to upload plans, photos and site videos after creating the project, in the Analyze tab."}
              </p>
            </div>
          </div>
        )}

        {/* STEP 4 — Review */}
        {step === 4 && (
          <div data-testid="wizard-review">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">
              {es ? "Revisión final" : "Final review"}
            </h2>
            <dl className="grid md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <ReviewRow lbl={es ? "Módulo" : "Module"} val={f.module} />
              <ReviewRow lbl={es ? "Subsistemas" : "Subsystems"} val={f.subsystems.join(", ")} />
              <ReviewRow lbl={es ? "Nombre" : "Name"} val={f.name} highlight />
              <ReviewRow lbl={es ? "Compañía" : "Company"} val={f.company} />
              <ReviewRow lbl={es ? "Ubicación" : "Location"} val={f.location} />
              <ReviewRow lbl={es ? "Contacto" : "Contact"} val={f.contact_name} />
              <ReviewRow lbl="Email" val={f.contact_email} />
              <ReviewRow lbl={es ? "Teléfono" : "Phone"} val={f.contact_phone} />
              <ReviewRow lbl={es ? "Inicio" : "Start"} val={f.start_date} />
              <ReviewRow lbl={es ? "Fin" : "End"} val={f.end_date} />
            </dl>
            {f.sow_text && (
              <div className="mt-4 pt-4 border-t border-slate-100">
                <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-1">SOW</div>
                <p className="text-xs font-mono whitespace-pre-wrap text-slate-700 bg-slate-50 p-3 rounded-xl max-h-40 overflow-y-auto">
                  {f.sow_text}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Nav */}
      <div className="flex items-center justify-between mt-6">
        <button
          disabled={step === 0 || creating}
          onClick={() => setStep(s => Math.max(0, s - 1))}
          data-testid="wizard-prev-btn"
          className="px-4 py-2 border border-slate-300 rounded-xl text-sm hover:bg-slate-50 disabled:opacity-40 inline-flex items-center gap-1">
          <ChevronLeft size={14} /> {es ? "Atrás" : "Back"}
        </button>
        {step < STEPS.length - 1 ? (
          <button
            disabled={!canNext()}
            onClick={() => setStep(s => s + 1)}
            data-testid="wizard-next-btn"
            className="px-5 py-2 bg-[#003366] text-white rounded-xl text-sm hover:bg-[#002244] disabled:opacity-50 inline-flex items-center gap-1 shadow-sm">
            {es ? "Siguiente" : "Next"} <ChevronRight size={14} />
          </button>
        ) : (
          <button
            disabled={creating || !f.name}
            onClick={create}
            data-testid="wizard-create-btn"
            className="px-5 py-2 bg-[#EA580C] text-white rounded-xl text-sm hover:bg-[#C2410C] disabled:opacity-50 inline-flex items-center gap-2 shadow-sm">
            {creating ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
            {es ? "Crear proyecto" : "Create project"}
          </button>
        )}
      </div>
    </div>
  );
}

function ReviewRow({ lbl, val, highlight }) {
  return (
    <>
      <dt className="text-xs font-mono uppercase tracking-wider text-slate-500 self-center">{lbl}</dt>
      <dd className={`text-sm ${highlight ? "font-semibold text-slate-900" : "text-slate-700"}`}>
        {val || <span className="italic text-slate-400">—</span>}
      </dd>
    </>
  );
}
